import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import {
  GOLD,
  REF_BG,
  formatMediumDate,
  formatTime12,
  isElectricVehicle,
  productTypeCode,
  productTypeColor,
  terminalNumber,
  vehicleLabel,
} from "./tapUtils";

const Field = ({ label, value, multiline = false }) => (
  <Box mb={1.5}>
    <Typography sx={{ color: "#000", fontSize: "0.8rem", mb: 0.3 }}>{label}</Typography>
    <Typography sx={{ color: "#000", fontSize: "1rem", fontWeight: 600, whiteSpace: multiline ? "pre-wrap" : "normal" }}>
      {value || "—"}
    </Typography>
  </Box>
);

const Toggle = ({ label, checked }) => (
  <Box display="flex" flexDirection="column" alignItems="center" minWidth={88}>
    <Switch checked={Boolean(checked)} disabled color="secondary" />
    <Typography sx={{ color: "#000", fontSize: "0.78rem", fontWeight: 600 }}>{label}</Typography>
  </Box>
);

const BookingDetailDialog = ({ open, booking, onClose }) => {
  if (!booking) return null;
  const code = productTypeCode(booking.productType);
  const typeLabel = code === "SELF" ? "SP" : code;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: "#fff",
          color: "#000",
          "& .MuiTypography-root": { color: "#000" },
          "& .MuiTab-root": { color: "#000" },
          "& .Mui-selected": { color: "#000 !important" },
          "& .MuiInputLabel-root, & .MuiFormLabel-root": { color: "#000" },
          "& .MuiInputBase-input": { color: "#000", WebkitTextFillColor: "#000" },
          "& .MuiOutlinedInput-notchedOutline": { borderColor: "#000" },
          "& .MuiSvgIcon-root": { color: "#000" },
          "& .MuiButton-root": { color: "#000", borderColor: "#000" },
          "& .MuiButton-root.Mui-disabled": { color: "#000", borderColor: "#000", opacity: 1 },
          "& .MuiTab-root.Mui-disabled": { color: "#000", opacity: 1 },
        },
      }}
    >
      <Box sx={{ backgroundColor: "#fff", color: "#000" }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          px={2.5}
          py={1.5}
          borderBottom="1px solid #eee"
        >
          <Box display="flex" alignItems="center" gap={1}>
            <Box
              px={1.4}
              py={0.5}
              borderRadius="10px"
              bgcolor={REF_BG}
              color="#000"
              fontWeight={800}
            >
              {booking.referenceNumber}
            </Box>
            {booking.bookingStatus && (
              <Box
                px={1}
                py={0.4}
                borderRadius="6px"
                bgcolor="#eee"
                fontSize="0.75rem"
                fontWeight={700}
              >
                {booking.bookingStatus}
              </Box>
            )}
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Box
              px={0.9}
              py={0.4}
              borderRadius="4px"
              bgcolor={productTypeColor(code)}
              color="#000"
              fontWeight={800}
              fontSize="0.8rem"
            >
              {typeLabel}
            </Box>
            <Box
              px={1.3}
              py={0.45}
              borderRadius="10px"
              bgcolor={GOLD}
              color="#000"
              fontWeight={800}
            >
              {booking.reg || "TBC"}
            </Box>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        <Tabs
          value="booking"
          TabIndicatorProps={{ style: { backgroundColor: GOLD, height: 3 } }}
          sx={{
            px: 2,
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 700,
              color: "#000",
            },
            "& .Mui-selected": { color: "#000 !important" },
          }}
        >
          <Tab value="booking" label="Booking" />
          <Tab value="photos" label="Photos" disabled />
          <Tab value="timeline" label="Timeline" disabled />
          <Tab value="changes" label="Changes" disabled />
          <Tab value="charges" label="Charges" disabled />
        </Tabs>

        <DialogContent sx={{ backgroundColor: "#fff", pt: 2 }}>
          <Box mb={2.5}>
            <Typography fontWeight={800} mb={1.2} fontSize="1.05rem" color="#000">
              Customer details
            </Typography>
            <Box display="grid" gridTemplateColumns="repeat(3, 1fr)" gap={2}>
              <Field label="Name" value={booking.name} />
              <Field label="Mobile" value={booking.mobile} />
              <Field label="Email" value={booking.email} />
            </Box>
          </Box>

          <Box mb={2.5}>
            <Typography fontWeight={800} mb={1.2} fontSize="1.05rem" color="#000">
              Car details
            </Typography>
            <Box display="grid" gridTemplateColumns="repeat(3, 1fr)" gap={2}>
              <Field label="Make" value={vehicleLabel(booking)} />
              <Field label="Colour" value={booking.colour} />
              <Field label="Reg" value={booking.reg} />
            </Box>
          </Box>

          <Box mb={2.5}>
            <Typography fontWeight={800} mb={1} fontSize="1.05rem" color="#000">
              Departure
            </Typography>
            <Box display="grid" gridTemplateColumns="repeat(3, 1fr)" gap={2}>
              <TextField
                label="Date"
                value={formatMediumDate(booking.arrivalDateTime)}
                InputProps={{ readOnly: true }}
                size="small"
              />
              <TextField
                label="Time"
                value={formatTime12(booking.arrivalDateTime)}
                InputProps={{ readOnly: true }}
                size="small"
              />
              <TextField
                label="Terminal"
                value={terminalNumber(booking.arrivalTerminal)}
                InputProps={{ readOnly: true }}
                size="small"
              />
            </Box>
          </Box>

          <Box mb={2.5}>
            <Typography fontWeight={800} mb={1} fontSize="1.05rem" color="#000">
              Return
            </Typography>
            <Box display="grid" gridTemplateColumns="repeat(3, 1fr)" gap={2}>
              <TextField
                label="Date"
                value={formatMediumDate(booking.returnDateTime)}
                InputProps={{ readOnly: true }}
                size="small"
              />
              <TextField
                label="Time"
                value={formatTime12(booking.returnDateTime)}
                InputProps={{ readOnly: true }}
                size="small"
              />
              <TextField
                label="Terminal"
                value={terminalNumber(booking.returnTerminal)}
                InputProps={{ readOnly: true }}
                size="small"
              />
            </Box>
          </Box>

          <Box mb={2}>
            <TextField
              label="Flight nr"
              value={booking.rtnFlight || booking.outFlight || ""}
              InputProps={{ readOnly: true }}
              size="small"
              sx={{ maxWidth: 240 }}
            />
          </Box>

          <Box display="flex" flexWrap="wrap" gap={1} mb={2.5}>
            <Toggle label="Hand Luggage" />
            <Toggle label="CPT" />
            <Toggle label="Full Valet" />
            <Toggle label="EV" checked={isElectricVehicle(booking)} />
            <Toggle label="No Show" />
          </Box>

          <Box mb={2.5}>
            <TextField
              label="Notes"
              value={booking.productName ? `${booking.productName} · ${booking.airport || ""}` : ""}
              InputProps={{ readOnly: true }}
              fullWidth
              multiline
              minRows={3}
            />
          </Box>

          <Box display="flex" gap={2} mb={1}>
            <Button variant="outlined" disabled sx={{ flex: 1, py: 2, color: "#000", borderColor: "#000" }}>
              Ticket In
            </Button>
            <Button variant="outlined" disabled sx={{ flex: 1, py: 2, color: "#000", borderColor: "#000" }}>
              Ticket Out
            </Button>
          </Box>
        </DialogContent>
        <Box height={8} bgcolor="#7E57C2" />
      </Box>
    </Dialog>
  );
};

export default BookingDetailDialog;
